import dayjs from 'dayjs';

import {
  type MarketplaceRootData,
  marketplaceRootDefaultValues,
} from '../schemas/rootSchema';
import {
  type MarketplaceAcquisitionTypeItem,
  type MarketplaceOfferGroup,
  type MarketplaceProductResponse,
  type MarketplacePropertiesResponse,
  AcquisitionType,
  CopyType,
} from '../types/types';

/** Maps the backend `code` values from acquisitionTypes to the frontend AcquisitionType enum. */
const acquisitionCodeToType: Record<string, AcquisitionType> = {
  compra: AcquisitionType.Purchase,
  assinatura: AcquisitionType.Subscription,
  aluguel: AcquisitionType.Rental,
};

type AudienceKey = 'subscriber' | 'freemium';

function mapOfferToFormOffer(offer?: MarketplaceOfferGroup['offers'][number]) {
  return {
    basePaymentOfferId: offer?.basePaymentOfferId ?? '',
    promotionalOffer: Boolean(offer?.tempPaymentOfferId),
    tempPaymentOfferId: offer?.tempPaymentOfferId ?? '',
    tag: offer?.tag ?? '',
    payOffer: offer?.payOffer,
  };
}

function resolveAcquisitionType(
  acquisitionId: number | undefined,
  acquisitionTypeItems: MarketplaceAcquisitionTypeItem[]
): AcquisitionType | undefined {
  if (acquisitionId === undefined) return undefined;
  const item = acquisitionTypeItems.find(a => a.id === acquisitionId);
  return item?.code ? acquisitionCodeToType[item.code] : undefined;
}

function applyOfferGroupsToAudience(
  formData: MarketplaceRootData,
  targetMarket: AudienceKey,
  audienceGroups: MarketplaceOfferGroup[],
  acquisitionTypeItems: MarketplaceAcquisitionTypeItem[]
) {
  const audience = formData.configuration[targetMarket];

  audience.enabled = audienceGroups.length > 0;
  audience.acquisitionTypes = [];

  audienceGroups.forEach(group => {
    const acquisitionId = group.offers?.[0]?.payOffer?.acquisitionId;
    const acquisitionType = resolveAcquisitionType(
      acquisitionId,
      acquisitionTypeItems
    );

    if (acquisitionType === AcquisitionType.Purchase) {
      audience.acquisitionTypes.push(AcquisitionType.Purchase);
      audience.purchase = {
        title: group.title ?? '',
        subtitle: group.subtitle ?? '',
        ...mapOfferToFormOffer(group.offers?.[0]),
      };
    } else if (acquisitionType === AcquisitionType.Subscription) {
      audience.acquisitionTypes.push(AcquisitionType.Subscription);
      audience.subscription = {
        title: group.title ?? '',
        subtitle: group.subtitle ?? '',
        offers: (group.offers ?? []).map(mapOfferToFormOffer),
      };
    } else if (acquisitionType === AcquisitionType.Rental) {
      audience.acquisitionTypes.push(AcquisitionType.Rental);
      audience.rental = {
        title: group.title ?? '',
        subtitle: group.subtitle ?? '',
        offers: (group.offers ?? []).map(mapOfferToFormOffer),
      };
    }
  });
}

function mapCreation(
  formData: MarketplaceRootData,
  product: MarketplaceProductResponse
): void {
  formData.creation.productType = product.type;
  formData.creation.title = product.title ?? '';
  formData.creation.description = product.description ?? '';
  formData.creation.buttonText = product.productDetails?.buttonText ?? '';
  formData.creation.categoryIds = [product.category?.id ?? ''];
  formData.creation.laneIds = [...(product.lanes.map(lane => lane.id) ?? [])];

  const creationImages = formData.creation.images as any;
  creationImages.program.card.preview = product.images?.program?.card ?? '';
  creationImages.program.logo.preview = product.images?.program?.logo ?? '';
  creationImages.program.vertical.preview =
    product.images?.program?.vertical ?? '';
  creationImages.combo.main.preview = product.images?.combo?.main ?? '';
  creationImages.combo.search.preview = product.images?.combo?.search ?? '';
  creationImages.combo.highlight.original.preview =
    product.images?.combo?.highlight?.original ?? '';
  creationImages.combo.highlight.small.preview =
    product.images?.combo?.highlight?.small ?? '';
}

function mapContent(
  formData: MarketplaceRootData,
  product: MarketplaceProductResponse
): void {
  const landingPageData = product.productDetails?.landingPageData;
  const copy = landingPageData?.copy ?? [];

  const userAccessSection = copy.find(
    item => item.type === CopyType.UserAccess
  );
  const reviewSection = copy.find(item => item.type === CopyType.Reviews);
  const instructorSection = copy.find(
    item => item.type === CopyType.Instructor
  );

  const teaser = landingPageData?.teaser ?? '';
  const teaserImage = product.images?.landingPage?.teaser ?? '';

  formData.content.mediaType = teaser || !teaserImage ? 'video' : 'image';
  formData.content.teaser = teaser;
  formData.content.mainImage = { preview: teaserImage, file: undefined };
  formData.content.durationTime = landingPageData?.durationTime ?? '';
  formData.content.programLength = landingPageData?.programs?.length ?? '';
  formData.content.level = landingPageData?.level ?? '';
  formData.content.objective = landingPageData?.objective ?? '';
  formData.content.mainTitle = landingPageData?.title ?? '';
  formData.content.description = landingPageData?.description ?? '';
  formData.content.buttonText = landingPageData?.buttonText ?? '';
  formData.content.benefits = [...(userAccessSection?.items ?? [])];
  formData.content.reviews = [...(reviewSection?.reviews ?? [])];
  formData.content.reviewsSectionTitle = reviewSection?.title ?? '';
  formData.content.instructor = {
    name: instructorSection?.instructor?.name ?? '',
    cref: instructorSection?.instructor?.cref ?? '',
    biography: instructorSection?.paragraphs?.join('\n') ?? '',
    image: {
      preview:
        product.images?.landingPage?.program?.instructor ??
        instructorSection?.instructor?.image ??
        '',
      file: undefined,
    },
  };

  const comboCarouselImages =
    product.images?.landingPage?.combo?.whatYouGet ?? [];
  formData.content.programImages = comboCarouselImages.map((url, i) => ({
    uid: `existing-${i}`,
    name: `carrossel-${i + 1}`,
    status: 'done',
    thumbUrl: url,
  }));
}

function mapSchedule(
  formData: MarketplaceRootData,
  releaseDate: ReturnType<typeof dayjs> | null
): void {
  formData.schedule.publishType = releaseDate ? 'scheduled' : 'now';
  formData.schedule.scheduledDate = releaseDate;
  formData.schedule.scheduledTime = releaseDate;
}

function mapConfiguration(
  formData: MarketplaceRootData,
  product: MarketplaceProductResponse,
  acquisitionTypeItems: MarketplaceAcquisitionTypeItem[]
): void {
  const offerGroups = product.offerGroups ?? [];

  const subscriberGroups = offerGroups.filter(
    group => group.offers?.[0]?.targetMarket === 'subscriber'
  );
  const freemiumGroups = offerGroups.filter(
    group => group.offers?.[0]?.targetMarket === 'freemium'
  );

  applyOfferGroupsToAudience(
    formData,
    'subscriber',
    subscriberGroups,
    acquisitionTypeItems
  );
  applyOfferGroupsToAudience(
    formData,
    'freemium',
    freemiumGroups,
    acquisitionTypeItems
  );
}

export function toMarketplaceFormValues(
  product: MarketplaceProductResponse,
  properties?: MarketplacePropertiesResponse
): MarketplaceRootData {
  const formData: MarketplaceRootData = JSON.parse(
    JSON.stringify(marketplaceRootDefaultValues)
  );

  const releaseDate = product.releaseDate ? dayjs(product.releaseDate) : null;
  const acquisitionTypeItems = properties?.acquisitionTypes ?? [];

  mapCreation(formData, product);
  mapContent(formData, product);
  mapSchedule(formData, releaseDate);
  mapConfiguration(formData, product, acquisitionTypeItems);

  return formData;
}
